import { c as _c } from "react/compiler-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../../dropdown";
import { FilterPill } from "../../filter-pill";
import Tooltip from "../../tooltip";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoIosClose } from "react-icons/io";
import Button from "@/components/ui/button";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { cx } from "@/lib/tailwind-utils";
import { FilterEditContext, type FilterEditContextValue } from "./edit-context";
import { formatFilterDisplayValue } from "./helpers";
import { useFilterFocus } from "./hooks";
import { FilterInput, filterContentWidthClass } from "./render-input";
import type { FilterTypeProps } from "./types";
const isEmptyValue = (value: any) => value === undefined || value === null || value === "" || Array.isArray(value) && value.length === 0;

/**
 * InlinePillTableFilter — renders a filter as a chip outside the main
 * Filters dropdown.
 *
 * Write semantics: edits buffer in local state and publish to `filterSetter`
 * on close (blur). This keeps the table from refetching on every keystroke
 * and keeps the pill mounted while editing.
 */
const InlinePillTableFilter: React.FC<FilterTypeProps> = t0 => {
  const $ = _c(67);
  const {
    label,
    type,
    filterValue,
    filterSetter,
    options,
    className,
    disableClear: t1,
    icon: Icon,
    onBack,
    direction,
    openEnded
  } = t0;
  const disableClear = t1 === undefined ? false : t1;
  const {
    focusRef,
    inputRef
  } = useFilterFocus();
  const clock = useClinicClock();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(filterValue);
  const pendingValueRef = useRef(filterValue);
  const justClearedRef = useRef(false);
  let t2;
  let t3;
  if ($[0] !== filterValue || $[1] !== isOpen) {
    t2 = () => {
      if (!isOpen) {
        pendingValueRef.current = filterValue;
        setPendingValue(filterValue);
      }
    };
    t3 = [filterValue, isOpen];
    $[0] = filterValue;
    $[1] = isOpen;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      pendingValueRef.current = value;
      setPendingValue(value);
      justClearedRef.current = false;
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleChange = t4;
  let t5;
  if ($[5] !== filterSetter || $[6] !== filterValue) {
    t5 = open => {
      setIsOpen(open);
      if (open) {
        pendingValueRef.current = filterValue;
        setPendingValue(filterValue);
        justClearedRef.current = false;
        return;
      }
      if (justClearedRef.current) {
        justClearedRef.current = false;
        return;
      }
      const next = isEmptyValue(pendingValueRef.current) ? undefined : pendingValueRef.current;
      const unchanged = next === undefined && isEmptyValue(filterValue) || isEqual(next, filterValue);
      if (!unchanged) {
        filterSetter(next);
      }
    };
    $[5] = filterSetter;
    $[6] = filterValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[8] !== filterSetter) {
    t6 = e => {
      e.stopPropagation();
      pendingValueRef.current = undefined;
      setPendingValue(undefined);
      justClearedRef.current = true;
      filterSetter(undefined);
    };
    $[8] = filterSetter;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleClearFilter = t6;
  let t7;
  if ($[10] !== pendingValue) {
    t7 = {
      value: pendingValue
    };
    $[10] = pendingValue;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== handleOpenChange) {
    t8 = {
      onChange: handleChange,
      submit: () => handleOpenChange(false)
    };
    $[12] = handleOpenChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== direction || $[15] !== focusRef || $[16] !== label || $[17] !== onBack || $[18] !== openEnded || $[19] !== options || $[20] !== type) {
    t9 = {
      type,
      label,
      options,
      onBack,
      focusRef,
      direction,
      openEnded
    };
    $[14] = direction;
    $[15] = focusRef;
    $[16] = label;
    $[17] = onBack;
    $[18] = openEnded;
    $[19] = options;
    $[20] = type;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = {
      state: t7,
      actions: t8,
      meta: t9
    };
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const editContextValue = t10;
  let t11;
  if ($[26] !== inputRef) {
    t11 = e_0 => {
      if (e_0.target !== e_0.currentTarget) {
        return;
      }
      const el = e_0.currentTarget;
      if (el instanceof HTMLElement && el.dataset.state === "open" && inputRef.current) {
        inputRef.current.focus();
      }
    };
    $[26] = inputRef;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleContentAnimationEnd = t11;
  if (options?.length === 0) {
    return null;
  }
  const displaySource = isOpen ? pendingValue : filterValue;
  let t12;
  if ($[28] !== clock || $[29] !== displaySource || $[30] !== options || $[31] !== type) {
    t12 = formatFilterDisplayValue(displaySource, type, options, clock);
    $[28] = clock;
    $[29] = displaySource;
    $[30] = options;
    $[31] = type;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const {
    fullDisplayValue,
    truncatedDisplayValue
  } = t12;
  let t13;
  if ($[33] !== disableClear || $[34] !== displaySource || $[35] !== handleClearFilter) {
    t13 = !isEmptyValue(displaySource) && !disableClear ? <Button variant="ghost" icon={IoIosClose} onMouseDown={handleClearFilter} className="relative z-20 mr-1 !p-0" /> : null;
    $[33] = disableClear;
    $[34] = displaySource;
    $[35] = handleClearFilter;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const clearButton = t13;
  let t14;
  if ($[37] !== Icon) {
    t14 = Icon ? <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" /> : null;
    $[37] = Icon;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== displaySource || $[40] !== fullDisplayValue || $[41] !== truncatedDisplayValue) {
    t15 = !isEmptyValue(displaySource) && truncatedDisplayValue && <><div className="mx-2 h-3 self-center border-l" /><Tooltip content={fullDisplayValue} triggerAsChild={true}><span className="text-caption-emphasis text-primary">{truncatedDisplayValue}</span></Tooltip></>;
    $[39] = displaySource;
    $[40] = fullDisplayValue;
    $[41] = truncatedDisplayValue;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== label || $[44] !== t14 || $[45] !== t15) {
    t16 = <>{t14}{label}{t15}</>;
    $[43] = label;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const triggerContent = t16;
  let t17;
  if ($[47] !== triggerContent) {
    t17 = <DropdownMenuTrigger className="flex items-center">{triggerContent}</DropdownMenuTrigger>;
    $[47] = triggerContent;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== className || $[50] !== type) {
    t18 = cx("min-w-0 overflow-visible", filterContentWidthClass(type), className);
    $[49] = className;
    $[50] = type;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="min-w-[min(200px,calc(var(--radix-popper-available-width)-12px))] px-1.5 py-1"><FilterInput /></div>;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== editContextValue) {
    t20 = <FilterEditContext value={editContextValue}>{t19}</FilterEditContext>;
    $[53] = editContextValue;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== handleContentAnimationEnd || $[56] !== t18 || $[57] !== t20) {
    t21 = <DropdownMenuContent className={t18} align="start" onAnimationEnd={handleContentAnimationEnd}>{t20}</DropdownMenuContent>;
    $[55] = handleContentAnimationEnd;
    $[56] = t18;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== handleOpenChange || $[60] !== isOpen || $[61] !== t17 || $[62] !== t21) {
    t22 = <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>{t17}{t21}</DropdownMenu>;
    $[59] = handleOpenChange;
    $[60] = isOpen;
    $[61] = t17;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== clearButton || $[65] !== t22) {
    t23 = <FilterPill as="div" className="w-auto overflow-hidden whitespace-nowrap px-2 py-1 shadow-sm transition-all duration-300">{clearButton}{t22}</FilterPill>;
    $[64] = clearButton;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
};
export default InlinePillTableFilter;